import { type MouseEvent } from 'react'
import { Box } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

export type SoapCheckboxState = 'checked' | 'unchecked'

interface Props {
  state: SoapCheckboxState
  onToggle?: () => void
  // interactive (onToggle あり) の場合は必須. 非 interactive (表示専用) の場合は装飾要素として
  // aria-hidden にするため未指定でよい.
  ariaLabel?: string
  size?: number
}

const INNER_SIZE = 14

// 太いストローク (strokeWidth 2) の自前 checkmark/dash. MUI の `CheckIcon`/`RemoveIcon` は
// 14px 前後だと線が細く「チェックボックスっぽくない」と判定された (実機確認 2026-07-01) ため、
// 塗りつぶし glyph ではなく線描画で組む (= 枠は薄く、 印は太く、 のメリハリを付ける).
const CheckMark = ({ color }: { color: string }) => (
  <svg width={10} height={10} viewBox="0 0 16 16" fill="none">
    <path
      d="M3.5 8.5L6.3 11.3L12.5 4.7"
      stroke={color}
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// SOAP 行で共有する自前描画チェックボックス. MUI 標準 `<Checkbox>` は
// Material chrome (ripple・44px タッチ領域・既定の彩度高い fill) が Notion ライク正典と
// 相性が悪く、 過去に「50行ぶんのチェックボックス列が視覚的に重い」と判定され撤去された経緯が
// ある (log/170 work-log/03 §0)。 その反省を踏まえ、 チェック済み (= デフォルト/多数派状態) を
// 含め彩度の高い accent 色を一切使わず、 無彩色 (fg 系 token) のみで組む
// (正典 §1.1: 彩度の高い色は状態を示すときのみ. 除外の主信号は本文側の取消線+淡色が担う).
// サイズは正典アイコンサイズ下限 (14px) 相当まで絞り、 存在感を抑える
// (実機確認 2026-07-01: 初期案の 26/16px は「存在感大きすぎ」と判定された).
// 黒背景+白文字の塗りつぶしにすると、 色相を彩度で逃れても「毎行が黒四角」という別の重さが
// 再発する一方 (実機確認 2026-07-01)、 枠線もアイコンも同程度に薄いと今度は「チェックボックス
// に見えない」に振れすぎる (同日再確認). そのため 枠線は薄く (fg.faint) / チェック印は太く
// くっきり (fg.default, 線描画) とメリハリを付ける.
export const SoapCheckbox = ({
  state,
  onToggle,
  ariaLabel,
  size = 22,
}: Props) => {
  const interactive = Boolean(onToggle)
  const handleClick = interactive
    ? (e: MouseEvent) => {
        e.stopPropagation()
        onToggle?.()
      }
    : undefined

  return (
    <Box
      component={interactive ? 'button' : 'span'}
      role={interactive ? 'checkbox' : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      aria-hidden={interactive ? undefined : true}
      aria-checked={interactive ? state === 'checked' : undefined}
      onClick={handleClick}
      sx={{
        appearance: 'none',
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        flexShrink: 0,
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: interactive ? 'pointer' : 'default',
        borderRadius: `${RADIUS.sm}px`,
        transition: 'background-color 120ms',
        ...(interactive && {
          '&:hover': { bgcolor: colorTokens.overlay.hover },
          '&:focus': { outline: 'none' },
          '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
        }),
      }}
    >
      <Box
        sx={{
          width: INNER_SIZE,
          height: INNER_SIZE,
          borderRadius: `${RADIUS.sm}px`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${colorTokens.fg.faint}`,
          bgcolor: 'transparent',
        }}
      >
        {state === 'checked' && <CheckMark color={colorTokens.fg.default} />}
      </Box>
    </Box>
  )
}
